import type {
  DressingWeight,
  IngredientKind,
  MixedDressingDetails,
} from '@sg/graphql-schema';

import {
  type Product,
  type ProductIngredientAssetVersion,
  type ProductLineItem,
  type ProductModification,
  type ProductRestaurant,
} from './remote-data';

// ─────────────────────────────────────────────────────────────────────────────

export type Context = {
  //
  // ─── External Data ───────────────────────────

  productSlug: string;
  restaurantSlug: string;
  lineItemId: string | undefined;
  ingredientAssetVersion: ProductIngredientAssetVersion;
  isDelivery: boolean;

  product: Product | undefined;
  productRestaurant: ProductRestaurant | undefined;
  productLineItem: ProductLineItem | undefined;

  quantity: number;

  // ─── Static Data ─────────────────────────────

  modificationLimits: ModificationGroupLimits;
  modificationGroups: ExtendedModifierGroup[] | undefined;
  allModificationsMap: ModificationsMap | undefined;

  // ─── Dynamic Data ────────────────────────────

  modifications: Modifications | undefined;
  mixedDressingDetails: MixedDressingDetails[];
  calories: number;
  netPrice: number;

  lastKnownModifications: Modifications | undefined;
  lastKnownMixedDressingDetails: MixedDressingDetails[];

  customName: string | undefined;
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type ModificationsMap = Map<string, Modification>;
export type ModificationsList = Modification[];
export type Modification = ProductModification & {
  quantity: number;
  modificationGroupId: string;
  modificationGroupName: string;
  modificationGroupDisplayName: string;
  modificationGroupOrder: number;
  order: number;
};

export type ExtendedModifierGroup = Product['modifierGroups'][number] & {
  modificationIngredientKind: IngredientKind | null | undefined;
};

export type Modifier = {
  ingredientId: string;
  additionCost: number;
  removalCost: number;
  substitutionCost: number;
  subKind: string | null;
};

export type SubstitutionModifier = {
  removedIngredientId: string;
  addedIngredientId: string;
} & Omit<Modifier, 'ingredientId'>;

export type Modifications = {
  initial: Modification[];
  active: Modification[];
} & Modifiers;

export type Modifiers = {
  additions: Modifier[];
  removals: Modifier[];
  substitutions: SubstitutionModifier[];
};

export type ModificationGroupLimits = Record<string, GroupLimits>;

export type GroupLimits = {
  displayName: string;
  allowMultipleQuantity: boolean;
  minAggregateQuantity: number;
  maxAggregateQuantity: number;
  freeAggregateQuantity: number;
};

export type CombinedDressingDetails = {
  id: string;
  name: string;
  portionsNumber: number;
  weight: DressingWeight | undefined;
  modification: Modification;
};
